import React from 'react';

/**
 * METER — the one quiet horizontal bar behind every allowance readout in Settings (credits, storage).
 * Extracted when storage got its own bar so the two cannot drift apart visually.
 *
 * Presentational: it renders a percentage and nothing else. The caller decides what the percentage
 * MEANS (credits count DOWN as they are spent, storage counts UP as it fills) and writes the line of
 * plain language underneath.
 */
export interface MeterProps {
  /** 0-100. Clamped by the caller's own maths; clamped again here so a bad input cannot overflow. */
  percent: number;
  /** Accessible name of the bar, e.g. "Credits remaining". */
  label: string;
  /** No fill and no value while the figure is still loading. */
  loading?: boolean;
  /** Test id of the fill element: `${testId}-fill`. */
  testId: string;
}

const Meter: React.FC<MeterProps> = ({ percent, label, loading, testId }) => {
  const filled = Math.max(0, Math.min(100, Math.round(percent)));
  return (
    <div
      className="h-2 w-full overflow-hidden rounded-full bg-secondary-200 dark:bg-secondary-700"
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={loading ? undefined : filled}
    >
      {!loading && (
        <div
          className="h-full rounded-full bg-secondary-900 dark:bg-white transition-[width] duration-500 ease-out"
          style={{ width: `${filled}%` }}
          data-testid={`${testId}-fill`}
        />
      )}
    </div>
  );
};

export default Meter;
